import { type Easing, type MotionStyle, motion } from "framer-motion"
import { useMemo } from "react"
import { getBaseBlurPx, getScale, stadiumLightsConfig } from "./data/stadium-lights-config"
import { useStadiumLightsEffects } from "./hooks/use-stadium-lights-effects"
import { useStadiumLightsOpacity } from "./hooks/use-stadium-lights-opacity"

/**
 * A lightweight, self-contained version of the landing-page stadium lights.
 *
 * Behaviour
 *   • Start at full brightness (opacity 1) at the top of the page.
 *   • Dim and brighten in a curve as user scrolls through content.
 *   • Maintain full brightness through middle sections.
 *   • Dim to 40% near bottom, then ramp back up to full brightness.
 */
export default function LandingBackgroundStadiumLights() {
  const opacity = useStadiumLightsOpacity()
  const { flashActive, ctaPressActive } = useStadiumLightsEffects()

  // Derive a scale so the lights look consistent across break-points.
  const width = typeof window !== "undefined" ? window.innerWidth : 1280
  const scale = getScale(width)

  // Apply full brightness while CTA is actively pressed
  const effectiveOpacity = ctaPressActive ? 1 : flashActive ? 1 : opacity

  const baseBlurPx = getBaseBlurPx(width)
  const blurPx = flashActive ? baseBlurPx * 2 : baseBlurPx

  const baseStyle: MotionStyle = useMemo(
    () => ({
      backgroundImage: `url('${stadiumLightsConfig.image.url}')`,
      backgroundSize: stadiumLightsConfig.image.size,
      backgroundPosition: stadiumLightsConfig.image.position,
      WebkitMaskImage: stadiumLightsConfig.mask.radialGradient,
      maskImage: stadiumLightsConfig.mask.radialGradient,
      opacity: effectiveOpacity,
      transition: stadiumLightsConfig.animation.opacity.transition,
    }),
    [effectiveOpacity],
  )

  // Add blue tint to the lights with hue-rotate and slight saturation boost
  // IMPORTANT: Keep the exact filter string format from original
  const filterBlur = `blur(${blurPx}${stadiumLightsConfig.blur.suffix}) hue-rotate(${stadiumLightsConfig.blur.hueRotate}deg) saturate(${stadiumLightsConfig.blur.saturate})`

  // Create the animation filter arrays
  const animateFilter = useMemo(
    () =>
      stadiumLightsConfig.animation.brightness.values.map((brightness) => `${filterBlur} brightness(${brightness})`),
    [filterBlur],
  )

  return (
    <>
      {/* Left light */}
      <motion.div
        className={stadiumLightsConfig.className.left}
        style={{
          ...baseStyle,
          transform: `scaleX(-1) scale(${scale})`,
        }}
        initial={{ filter: `${filterBlur} brightness(1)` }}
        animate={{ filter: animateFilter }}
        transition={{
          duration: stadiumLightsConfig.animation.brightness.duration,
          repeat: Number.POSITIVE_INFINITY,
          ease: stadiumLightsConfig.animation.brightness.ease as Easing,
        }}
        aria-hidden="true"
      />

      {/* Right light */}
      <motion.div
        className={stadiumLightsConfig.className.right}
        style={{
          ...baseStyle,
          transform: `scale(${scale})`,
        }}
        initial={{ filter: `${filterBlur} brightness(1)` }}
        animate={{ filter: animateFilter }}
        transition={{
          duration: stadiumLightsConfig.animation.brightness.duration,
          repeat: Number.POSITIVE_INFINITY,
          ease: stadiumLightsConfig.animation.brightness.ease as Easing,
        }}
        aria-hidden="true"
      />
    </>
  )
}
